import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import CommentField, { ExistingAttachedFile, AiAssistSendMeta } from "../../components/comment-field/CommentField";
import { isClientGroupPosition, isProducerGroupPosition, isMessageOnRightSide } from "../../utils/memberPositionUtils";
import { useShortcutAction } from "../../utils/keyboardShortcuts";
import ActionPanel from "../../components/bottom-app-bar/ActionPanel";
import { ProjectPrimaryActionSlot } from "../../components/bottom-app-bar/ProjectPrimaryAction";
import MessageItem from "../../components/message/MessageItem";
import SvgLoadingAnimation from "../../components/loading/index";
import InlineLoading from "../../components/loading/InlineLoading";
import TopAppBarHome, {
  TopAppBarFloat,
  TOP_APP_BAR_HOME_HEIGHT,
} from "../../components/top-app-bar/TopAppBarHome";
import { ProjectMenu } from "../../components/project/ProjectMenu";
import DropZoneOverlay from "../../components/drop-zone-overlay/DropZoneOverlay";
import UploadProgressBar from "../../components/upload-progress/UploadProgressBar";
import { useUploadFailureDialog } from "../../components/upload-progress/useUploadFailureDialog";
import { useGetProjectMessagesQuery, useLazyGetProjectMessagesQuery, useLazyGetProjectMessageDetailQuery, useSendProjectMessageMutation, useDeleteProjectMessageMutation, useUpdateProjectMessageMutation, useMarkMessagesAsReadMutation, useMarkFilesAsDownloadedMutation, ProjectMessage, ProjectMessageFile, ProjectMessageFolder, ProjectMessageUser } from "../../app/services/projectMessages";
import TalkRoomFileTable from "./components/FileTable/TalkRoomFileTable";
import { MessageData } from "../../types/message";
import TypingIndicator from "../../components/typing-indicator/TypingIndicator";
import {
  useMessengerReconnect,
  useMessengerSocket,
  useMessengerSubscribe,
} from "../../app/messenger/MessengerSocketContext";
import { useTypingSender } from "../../hooks/useTypingSender";
import type { MessengerIncomingEvent } from "../../types/messengerEvents";
import { extractDetailEntity } from "../../utils/detailResponse";
import { useSyncUserDirectory } from "../../hooks/useSyncUserDirectory";
import { collectMessengerUsers } from "../../utils/collectMessengerUsers";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import useS3Upload, { UploadingFile, ValidationError, FileWithPath } from "../../hooks/useS3Upload";
import { extractS3KeyFromUrl } from "../../utils/s3";
import { motion, LayoutGroup } from "framer-motion";
import toast from "react-hot-toast";
import { useAnimatedMessageResolve } from "../../hooks/useAnimatedMessageResolve";
import { useObservedHeight } from "../../hooks/useObservedHeight";
import { useTimestampSeek } from "../../hooks/useTimestampSeek";
import { useBatchedCall } from "../../hooks/useBatchedCall";

// BottomAppBar の高さ。ActionPanel が position: fixed でこの分だけ上に浮いているため、
// メッセージエリアの下端 padding にも同じ分を加算する必要がある。
const BOTTOM_APP_BAR_HEIGHT = 80;
// メッセージと CommentField の間に最低限確保する視覚的マージン。
const TALK_ROOM_BOTTOM_PADDING_BUFFER = 32;
// ResizeObserver 初回計測前の短い瞬間でも余白が確保されるよう、最低値を保持する。
const TALK_ROOM_BOTTOM_PADDING_MIN = 112;

// 1メッセージあたりの添付ファイル数上限。BE 側（project_messages.py の
// MAX_FILES_PER_MESSAGE）と必ず同値に保つこと。超過分はアップロード前に弾く。
const MAX_FILES_PER_MESSAGE = 500;

// フィルターモードの型定義
// - files: ファイル一覧（ストレージビュー）。セグメント左端のアイコンで切り替える
type FilterMode = "in_progress" | "completed" | "search" | "files";

// Styled components for TalkRoom
const TalkRoomContainer = styled.div.attrs({
  className: 'u-col-center',
})`
  height: 100%;  /* 親のOutletContainerの高さを継承 */
  overflow: hidden;
  position: relative;
`;

// スクロール層。幅制限（.u-wrapper-reading）と分離してパネル全幅で持つことで、
// スクロールバーを読み幅カラムの外側（パネル右端）に置き、右寄せアバターの
// box-shadow がカラム外のガターへ自然に描画されるようにする
// （overflow-y: scroll は横方向もクリップするため、同一要素に幅制限があると影が切れる）。
const TalkRoomScroll = styled.div<{ $bottomPadding: number }>`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  /* 上端はフロートするセグメントコントロールの分だけ空ける。overflow の
   * クリップ境界は padding-box なので、スクロール中のメッセージはこの余白を
   * 通過してバナー直下まで描画される（= コントロールの下をくぐる） */
  padding-top: ${TOP_APP_BAR_HOME_HEIGHT}px;
  padding-bottom: ${({ $bottomPadding }) => $bottomPadding}px;

  /* パフォーマンス最適化 */
  will-change: scroll-position;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`;

// 読み幅カラム。スクロールは親の TalkRoomScroll が担うため overflow は持たない。
// 親が全幅のスクロール層になったので margin-inline: auto で中央寄せする。
const TalkRoomContent = styled.div.attrs({
  className: 'u-wrapper-reading',
})`
  margin-inline: auto;
  display: flex;
  flex-direction: column;
`;

// メッセージリストの最後に追加する余白要素
const MessageEndSpacer = styled.div`
  height: 64px;
  width: 100%;
  flex-shrink: 0;
`;

// アップロードモーダルのタイトル
const UploadModalTitle = styled.h3.attrs({
  className: 'heading-13',
})`
  margin: 0;
  text-align: center;
  color: var(--soremo-text);
`;

// メッセージを時系列順（古い→新しい）でソートするヘルパー関数
const sortMessagesByDate = (messages: ProjectMessage[]): ProjectMessage[] => {
  return [...messages].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
};

const TalkRoom: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  
  // 状態管理
  const [allMessages, setAllMessages] = useState<ProjectMessage[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<ProjectMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState<ProjectMessage | null>(null);
  const [messageToReply, setMessageToReply] = useState<ProjectMessage | null>(null);
  
  // フィルタリング用の状態
  const [filterMode, setFilterMode] = useState<FilterMode>("in_progress");
  const [searchText, setSearchText] = useState("");
  
  // CommentField 高さ監視: TalkRoomContent の padding-bottom を CommentField 実高さに追従させ、
  // 長文入力でテキストエリアが伸びても最下部メッセージが隠れないようにする。
  const {
    ref: commentFieldRef,
    height: commentFieldHeight,
  } = useObservedHeight<HTMLDivElement>();

  // CommentField は ActionPanel（position: fixed、BottomAppBar の上）に配置されるため、
  // CommentField 自身の高さ + BottomAppBar 分のオフセット + 余白 を下端 padding として確保する。
  const talkRoomBottomPadding = Math.max(
    TALK_ROOM_BOTTOM_PADDING_MIN,
    commentFieldHeight + BOTTOM_APP_BAR_HEIGHT + TALK_ROOM_BOTTOM_PADDING_BUFFER,
  );

  // ファイルアップロード用の状態
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // フォルダアップロード用の状態（パス情報付き）
  const [selectedFilesWithPath, setSelectedFilesWithPath] = useState<FileWithPath[]>([]);
  // 編集時の既存ファイル管理
  const [editExistingFiles, setEditExistingFiles] = useState<ExistingAttachedFile[]>([]);
  const [editFilesChanged, setEditFilesChanged] = useState(false);
  
  // S3アップロードフック
  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
    purpose: 'project_message',
    projectId,
    restrictions: { maxFiles: MAX_FILES_PER_MESSAGE },
    onValidationError: (errors: ValidationError[]) => {
      // 件数超過は1件にまとめて通知（フォルダ一括時に同種エラーが大量発生するため）
      const tooManyFiles = errors.some((error) => error.type === 'too_many_files');
      if (tooManyFiles) {
        toast.error(`一度に送信できるファイルは最大${MAX_FILES_PER_MESSAGE}件です`);
      }
      // 件数超過以外のエラー（形式・サイズ等）はファイルごとに通知
      errors
        .filter((error) => error.type !== 'too_many_files')
        .forEach((error) => {
          toast.error(`${error.file.name}: ${error.message}`);
        });
    },
    onFileComplete: (file: UploadingFile) => {
      console.log(`[Upload] アップロード完了: ${file.file.name}`);
    },
    onAllComplete: (files: UploadingFile[]) => {
      console.log(`[Upload] 全ファイルアップロード完了: ${files.length}件`);
      // 注意: selectedFilesのクリアはhandleSendMessage内で行っているため、
      // ここでクリアするとアップロード中に新たに選択されたファイルが消えてしまう
    },
    onError: (file: UploadingFile, error: Error) => {
      console.error(`[Upload] エラー: ${file.file.name}`, error);
    },
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });
  
  // API hooks
  const { data: initialMessagesData, isLoading: isLoadingMessages } = useGetProjectMessagesQuery(
    { project_id: projectId!, limit: 10, offset: 0 },
    { skip: !projectId }
  );
  
  const [fetchMoreMessages] = useLazyGetProjectMessagesQuery();
  const [fetchMessageDetail] = useLazyGetProjectMessageDetailQuery();
  const [sendMessage, { isLoading: isSending }] = useSendProjectMessageMutation();
  const [deleteMessage] = useDeleteProjectMessageMutation();
  const [updateMessage] = useUpdateProjectMessageMutation();
  const [markMessagesAsRead] = useMarkMessagesAsReadMutation();
  const [markFilesAsDownloaded] = useMarkFilesAsDownloadedMutation();
  
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, {
    skip: !projectId
  });

  // AI 支援機能の出し分け。
  // - チクチクフィルター: クライアント側（CLIENT / REVIEWER）
  // - 返信君: プロデューサー側（PRODUCER / DIRECTOR / ADMIN）
  const talkroomPosition = membershipData?.data?.position ?? "";
  const isTalkroomClientSide = isClientGroupPosition(talkroomPosition);
  const isTalkroomProducerSide = isProducerGroupPosition(talkroomPosition);
  
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const [isInitialScrollComplete, setIsInitialScrollComplete] = useState(false);
  const lastScrollTop = useRef(0);
  const isProcessingScroll = useRef(false);
  const previousScrollHeight = useRef(0);
  
  // allMessages の最新値を参照するref（コールバックの依存配列最適化用）
  const allMessagesRef = useRef(allMessages);
  allMessagesRef.current = allMessages;

  // 既読機能用のref
  const pendingReadMessageIds = useRef<Set<string>>(new Set());
  const readDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const messageElementRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const hasInitializedMessages = useRef(false);

  // プロジェクト切り替え時に既読関連のrefをリセット
  useEffect(() => {
    // projectIdが変更されたら、前のプロジェクトの既読キューをクリア
    pendingReadMessageIds.current.clear();
    messageElementRefs.current.clear();
    hasInitializedMessages.current = false;
    
    // タイマーもクリア
    if (readDebounceTimer.current) {
      clearTimeout(readDebounceTimer.current);
      readDebounceTimer.current = null;
    }
    
    // IntersectionObserverをリセット
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
  }, [projectId]);

  // 現在のユーザーID（既読判定用）
  const currentUserId = useMemo(() => {
    return currentUserData?.data?.id;
  }, [currentUserData]);

  // 現在のユーザー情報（既読登録後のローカルステート更新用）
  const currentUserForReceivers = useMemo((): ProjectMessageUser | null => {
    if (!currentUserData?.data) return null;
    const user = currentUserData.data;
    return {
      id: user.id,
      display_name: user.stage_name || user.fullname || 'Unknown',
      avatar: user.icon || null,
      position: membershipData?.data?.position || 'CLIENT',
    };
  }, [currentUserData, membershipData]);

  // 初期メッセージデータの読み込み（キャッシュ再取得による再実行を防止）
  useEffect(() => {
    if (initialMessagesData?.data?.messages && !hasInitializedMessages.current) {
      hasInitializedMessages.current = true;
      setAllMessages(sortMessagesByDate(initialMessagesData.data.messages));
      setHasMore(initialMessagesData.data.has_next);
      setCurrentOffset(initialMessagesData.data.next_offset || 0);
    }
  }, [initialMessagesData]);

  // 非表示メッセージ（受領ファイルの直接アップロード置き場）はタイムラインへ
  // 描画しない。ファイル一覧（TalkRoomFileTable）には allMessages ごと渡し、
  // fileTableUtils 側で受領ファイルとして表示する。
  const timelineMessages = useMemo(
    () => allMessages.filter((msg) => !msg.is_hidden),
    [allMessages],
  );

  // 進行中/完了の有無判定 → セグメントオプション生成
  const hasInProgressMessages = useMemo(
    () => timelineMessages.some((msg) => !msg.is_resolved),
    [timelineMessages],
  );
  const hasCompletedMessages = useMemo(
    () => timelineMessages.some((msg) => msg.is_resolved === true),
    [timelineMessages],
  );
  const talkroomFilterOptions = useMemo(() => {
    const options: { label: string; value: FilterMode }[] = [];
    if (hasInProgressMessages) {
      options.push({ label: "進行中", value: "in_progress" });
    }
    if (hasCompletedMessages) {
      options.push({ label: "完了", value: "completed" });
    }
    return options;
  }, [hasInProgressMessages, hasCompletedMessages]);
  const firstAvailableMode = talkroomFilterOptions[0]?.value ?? "in_progress";
  const availableValues = useMemo(
    () => talkroomFilterOptions.map((o) => o.value),
    [talkroomFilterOptions],
  );

  // フィルタリングされたメッセージを算出
  const filteredMessages = useMemo(() => {
    switch (filterMode) {
      case "in_progress":
        return timelineMessages.filter(msg => !msg.is_resolved);
      case "completed":
        return timelineMessages.filter(msg => msg.is_resolved === true);
      case "search": {
        if (!searchText.trim()) {
          return timelineMessages;
        }
        const lowerSearchText = searchText.toLowerCase();
        return timelineMessages.filter(msg =>
          msg.message.toLowerCase().includes(lowerSearchText)
        );
      }
      default:
        return timelineMessages;
    }
  }, [timelineMessages, filterMode, searchText]);

  const handleFilterModeChange = useCallback((mode: string) => {
    setFilterMode(prev => {
      // ファイル一覧表示中の検索はファイル名検索としてその場で行う
      // （メッセージ検索モードへは切り替えない）
      if (mode === "search" && prev === "files") return prev;
      return mode as FilterMode;
    });
  }, []);

  const handleSearchTextChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  useEffect(() => {
    if (filterMode === "search" || filterMode === "files") return;
    if (availableValues.includes(filterMode)) return;
    setFilterMode(firstAvailableMode);
  }, [filterMode, availableValues, firstAvailableMode]);

  // ⌥4 再押下（既にトークルーム表示中）: セグメント（進行中 → 完了 → ファイルリスト）
  // を循環切替する。存在しないセグメントはスキップ、検索モード中は先頭へ戻す。
  useShortcutAction("talkroom-cycle", () => {
    const order: FilterMode[] = [...availableValues, "files"];
    setFilterMode((prev) => order[(order.indexOf(prev) + 1) % order.length]);
  });

  // メッセージ表示位置判定（プレビューと共通のユーティリティ）。
  // 自分の投稿は membership の position 取得状況に関わらず常に右へ出す。
  const isMessageOnRight = useCallback(
    (message: ProjectMessage): boolean =>
      isMessageOnRightSide({
        authorId: message.author?.id,
        authorPosition: message.author?.position,
        currentUserId,
        viewerPosition: membershipData?.data?.position,
      }),
    [membershipData, currentUserId],
  );

  // シンプルなスクロール処理
  const scrollToBottom = useCallback(() => {
    if (!contentRef.current) return;
    
    // 最下部までスクロール
    contentRef.current.scrollTop = contentRef.current.scrollHeight;
  }, []);

  // 過去のメッセージを読み込む関数
  const loadMoreMessages = useCallback(async () => {
    if (!projectId || !hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const result = await fetchMoreMessages({
        project_id: projectId,
        limit: 10,
        offset: currentOffset,
      }).unwrap();
      
      if (result?.data?.messages && result.data.messages.length > 0) {
        // 重複を避けるため、既存のメッセージIDセットを作成
        setAllMessages(prev => {
          const existingIds = new Set(prev.map(msg => msg.id));
          const newMessages = result.data.messages.filter(msg => !existingIds.has(msg.id));
          // マージしてcreated_atでソート（古い順→新しい順）
          return sortMessagesByDate([...newMessages, ...prev]);
        });
        setHasMore(result.data.has_next);
        setCurrentOffset(result.data.next_offset || 0);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('[loadMoreMessages] エラー:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [projectId, hasMore, isLoadingMore, currentOffset, fetchMoreMessages]);

  // ファイル一覧表示中は過去メッセージを全件読み込む
  // （メッセージビューは無限スクロールで少しずつ読むが、ファイル一覧は
  //  全添付ファイルを揃える必要があるため、残りページを順次取得する）
  useEffect(() => {
    if (filterMode !== "files") return;
    if (!hasMore || isLoadingMore || isLoadingMessages) return;
    loadMoreMessages();
  }, [filterMode, hasMore, isLoadingMore, isLoadingMessages, loadMoreMessages]);

  // スクロールイベントハンドラ（無限スクロール対応）
  const handleScroll = useCallback(() => {
    if (isProcessingScroll.current || !contentRef.current) return;
    
    isProcessingScroll.current = true;
    
    const currentScrollTop = contentRef.current.scrollTop;
    
    // 初回スクロール完了後のみ、上部に近い場合に過去のメッセージを読み込む（無限スクロール）
    if (isInitialScrollComplete && currentScrollTop < 200 && hasMore && !isLoadingMore) {
      // スクロール位置を保存
      previousScrollHeight.current = contentRef.current.scrollHeight;
      loadMoreMessages();
    }
    
    // 現在のスクロール位置を保存
    lastScrollTop.current = currentScrollTop;
    
    // 処理フラグをリセット（スロットリング）
    setTimeout(() => {
      isProcessingScroll.current = false;
    }, 50);
  }, [isInitialScrollComplete, hasMore, isLoadingMore, loadMoreMessages]);

  // スクロールイベントの設定
  useEffect(() => {
    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        contentElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // 過去のメッセージを読み込んだ後、スクロール位置を保持
  // allMessagesの変更を監視し、メッセージが追加されたらスクロール位置を調整
  useEffect(() => {
    if (!contentRef.current || !isInitialScrollComplete) return;
    if (previousScrollHeight.current === 0) return;
    
    const newScrollHeight = contentRef.current.scrollHeight;
    const heightDifference = newScrollHeight - previousScrollHeight.current;
    
    if (heightDifference > 0) {
      // ユーザーが見ていた位置を維持（即座に調整）
      contentRef.current.scrollTop += heightDifference;
    }
    previousScrollHeight.current = 0;
  }, [allMessages, isInitialScrollComplete]);

  // ビューポートに余裕がある場合、自動的に過去メッセージを読み込む
  // コンテンツがビューポートを埋めるか、hasMoreがfalseになるまで繰り返す
  useEffect(() => {
    if (!contentRef.current || !isInitialScrollComplete) return;
    if (!hasMore || isLoadingMore) return;
    
    const contentElement = contentRef.current;
    const hasScrollRoom = contentElement.scrollHeight <= contentElement.clientHeight;
    
    // コンテンツがビューポートを埋めていない場合、過去メッセージを読み込む
    if (hasScrollRoom) {
      previousScrollHeight.current = contentElement.scrollHeight;
      loadMoreMessages();
    }
  }, [allMessages, hasMore, isLoadingMore, loadMoreMessages, isInitialScrollComplete]);

  // 初回メッセージ読み込み時に一番下にスクロール
  useEffect(() => {
    // 初回レンダリング時のみ最下部にスクロール
    if (initialMessagesData?.data?.messages && initialMessagesData.data.messages.length > 0 && isFirstRender.current) {
      isFirstRender.current = false;
      
      // スクロール処理を少し遅延させて、レンダリングが完了してから実行
      const scrollTimer = setTimeout(() => {
        if (contentRef.current) {
          scrollToBottom();
          
          // 十分に待ってから初回スクロール完了フラグを立てる（スクロール位置が安定するまで）
          setTimeout(() => {
            setIsInitialScrollComplete(true);
          }, 500);
        }
      }, 100);
      return () => clearTimeout(scrollTimer);
    }
  }, [initialMessagesData, scrollToBottom]);

  // アップロード完了時に自動でモーダルを閉じる。
  // エラー行が残っている場合は自動クリアしない（失敗が闇に消えないよう、
  // 再試行ボタン付きで表示し続け、失敗ダイアログの選択で解決する）
  useEffect(() => {
    if (uploadingFiles.length === 0) return;

    const allCompleted = uploadingFiles.every((f) => f.status === 'completed');

    if (allCompleted) {
      // 少し遅延させてユーザーに完了を見せてからクリア
      const timer = setTimeout(() => {
        clearCompleted();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  // メッセージ送信・更新ハンドラー
  const handleSendMessage = useCallback(async (messageText: string, meta?: AiAssistSendMeta) => {
    // メッセージもファイルもない場合は何もしない
    const hasMessage = messageText.trim() !== "";
    const hasFiles = selectedFiles.length > 0;
    const hasFilesWithPath = selectedFilesWithPath.length > 0;
    
    if (!projectId || (!hasMessage && !hasFiles && !hasFilesWithPath) || isSending || isUploading) return;
    
    // 編集モード
    if (messageToEdit) {
      if (!hasMessage && editExistingFiles.length === 0 && !hasFiles && !hasFilesWithPath) return;
      
      try {
        // 新規ファイルをアップロード
        let newFileKeys: string[] = [];
        if (hasFiles || hasFilesWithPath) {
          const normalFilesWithPath: FileWithPath[] = selectedFiles.map(file => ({
            file,
            relativePath: file.name,
          }));
          const allFilesToUpload: FileWithPath[] = [...normalFilesWithPath, ...selectedFilesWithPath];
          setSelectedFiles([]);
          setSelectedFilesWithPath([]);

          const uploadResults = await uploadFiles(allFilesToUpload);
          // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
          const hadFailures = uploadResults.some(f => f.status === 'error');
          const resolved = await resolveUploadFailures(uploadResults);
          if (resolved === null) {
            // キャンセル: 進捗を畳み、添付を選択状態へ戻して更新を中断
            clearCompleted();
            setSelectedFiles(selectedFiles);
            setSelectedFilesWithPath(selectedFilesWithPath);
            return false;
          }
          if (hadFailures) clearCompleted();
          newFileKeys = resolved
            .filter(f => f.fileKey)
            .map(f => f.fileKey as string);
        }

        // file_keys の計算（BE は「置換」セマンティクス）
        let fileKeysToSend: string[] | undefined;
        if (editFilesChanged || newFileKeys.length > 0) {
          const keptFiles = messageToEdit.files
            .filter(f => editExistingFiles.some(ef => ef.id === f.id));
          const existingFileKeys = keptFiles
            .map(f => f.file_key || extractS3KeyFromUrl(f.original_url))
            .filter((k): k is string => !!k);
          fileKeysToSend = [...existingFileKeys, ...newFileKeys];
        }

        const doUpdate = () => updateMessage({
          project_id: projectId,
          message_id: messageToEdit.id,
          message: messageText.trim(),
          file_keys: fileKeysToSend,
          ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
          ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
          ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
        }).unwrap();
        // 添付をアップロードした場合、反映が終わるまでは離脱を引き止める
        const result = newFileKeys.length > 0
          ? await withPostProcess(doUpdate, 'メッセージを更新中')
          : await doUpdate();
        
        if (result.data?.message) {
          setAllMessages(prev => prev.map(msg => 
            msg.id === messageToEdit.id ? result.data.message : msg
          ));
        }
        setMessageToEdit(null);
        setEditExistingFiles([]);
        setEditFilesChanged(false);
      } catch (error) {
        console.error('[handleSendMessage] メッセージ更新エラー:', error);
        toast.error('メッセージの更新に失敗しました');
      }
      return;
    }
    
    // ファイルキーを収集（ファイルアップロード後に使用）
    let fileKeys: string[] = [];
    
    // ファイルがある場合はアップロードを開始し、完了を待つ
    if (hasFiles || hasFilesWithPath) {
      try {
        // 通常のファイルとフォルダからのファイルをマージ
        // 通常のファイルはFileWithPath形式に変換（relativePathはファイル名のみ）
        const normalFilesWithPath: FileWithPath[] = selectedFiles.map(file => ({
          file,
          relativePath: file.name,
        }));
        const allFilesToUpload: FileWithPath[] = [...normalFilesWithPath, ...selectedFilesWithPath];
        
        // 選択ファイルをクリア（アップロード中はuploadingFilesで表示される）
        setSelectedFiles([]);
        setSelectedFilesWithPath([]);
        
        // アップロード完了を待ち、file_keysを取得
        const uploadResults = await uploadFiles(allFilesToUpload);

        // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
        const hadFailures = uploadResults.some((f) => f.status === 'error');
        const resolved = await resolveUploadFailures(uploadResults);
        if (resolved === null) {
          // キャンセル: 進捗を畳み、添付を選択状態へ戻して送信を中断
          //（本文は CommentField 側が復元する）
          clearCompleted();
          setSelectedFiles(selectedFiles);
          setSelectedFilesWithPath(selectedFilesWithPath);
          return false;
        }
        if (hadFailures) clearCompleted();

        // 成功したファイルからfile_keyを抽出
        fileKeys = resolved
          .filter((f) => f.fileKey)
          .map((f) => f.fileKey as string);

        // 続行対象がなく、メッセージもない場合は中断
        if (fileKeys.length === 0 && !hasMessage) {
          console.warn('[handleSendMessage] アップロードに失敗し、メッセージもないため送信をスキップ');
          return;
        }
      } catch (error) {
        console.error('[handleSendMessage] ファイルアップロードエラー:', error);
        toast.error('ファイルのアップロードに失敗しました');
        // メッセージがない場合は中断
        if (!hasMessage) return;
      }
    }
    
    // メッセージまたはファイルがある場合は送信処理（ファイルのみの場合は空メッセージ）
    // 通常の送信処理（返信の場合は parent_id を含む）
    try {
      const doSend = () => sendMessage({
        project_id: projectId,
        message: hasMessage ? messageText.trim() : '',
        parent_id: messageToReply?.id || null,
        file_keys: fileKeys.length > 0 ? fileKeys : undefined,
        ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
        ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
        ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
      }).unwrap();
      // 添付をアップロードした場合、送信が終わるまでは離脱を引き止める
      if (fileKeys.length > 0) {
        await withPostProcess(doSend, 'メッセージを送信中');
      } else {
        await doSend();
      }
      
      // 返信モードをリセット
      if (messageToReply) {
        setMessageToReply(null);
      }
      
      // 送信成功後、最新のメッセージ1件だけを取得して追加
      const latestResult = await fetchMoreMessages({
        project_id: projectId,
        limit: 1,
        offset: 0,
      }).unwrap();
      
      if (latestResult?.data?.messages?.[0]) {
        const newMessage = latestResult.data.messages[0];
        // 既存のメッセージに含まれていなければ追加
        setAllMessages(prev => {
          if (prev.some(msg => msg.id === newMessage.id)) {
            return prev;
          }
          return [...prev, newMessage];
        });
      }
      
      // 少し遅延させてから最下部にスクロール（レンダリング完了後）
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error('[handleSendMessage] メッセージ送信エラー:', error);
      toast.error('メッセージの送信に失敗しました');
    }
  }, [projectId, isSending, isUploading, messageToEdit, messageToReply, selectedFiles, selectedFilesWithPath, editExistingFiles, editFilesChanged, sendMessage, updateMessage, uploadFiles, resolveUploadFailures, clearCompleted, withPostProcess, fetchMoreMessages, scrollToBottom]);

  // 返信ハンドラー（返信モードに切り替え）
  const handleReply = useCallback((message: MessageData) => {
    setMessageToReply(message as ProjectMessage);
  }, []);
  
  // メッセージ編集ハンドラー（編集モードに切り替え）
  const handleEdit = useCallback((message: MessageData) => {
    const msg = message as ProjectMessage;
    setMessageToEdit(msg);
    setEditExistingFiles(
      msg.files.map(f => ({ id: f.id, fileName: f.file_name, fileType: f.file_type })),
    );
    setEditFilesChanged(false);
  }, []);
  
  // 解決アニメーション制御
  const { fadingOutEntry, executeWithAnimation } = useAnimatedMessageResolve();

  // 解決済み状態の切り替えハンドラー
  const handleResolve = useCallback(async (message: MessageData) => {
    if (!projectId) return;

    const direction = message.is_resolved ? "left" : "right";
    let updatedMessage: ProjectMessage | null = null;

    executeWithAnimation(
      message.id,
      direction,
      async () => {
        const result = await updateMessage({
          project_id: projectId,
          message_id: message.id,
          is_resolved: !message.is_resolved,
        }).unwrap();
        if (result.data?.message) {
          updatedMessage = result.data.message;
        }
      },
      () => {
        if (updatedMessage) {
          setAllMessages(prev => prev.map(msg =>
            msg.id === message.id ? updatedMessage! : msg
          ));
        }
      },
    );
  }, [projectId, updateMessage, executeWithAnimation]);

  // メッセージ削除ハンドラー（確認ダイアログを表示）
  const handleDelete = useCallback((message: MessageData) => {
    setMessageToDelete(message as ProjectMessage);
  }, []);

  // 削除確定処理
  const confirmDelete = useCallback(async () => {
    if (!messageToDelete || !projectId || isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteMessage({
        project_id: projectId,
        message_id: messageToDelete.id,
      }).unwrap();
      
      // 成功したらローカル状態から削除
      setAllMessages(prev => prev.filter(msg => msg.id !== messageToDelete.id));
      setMessageToDelete(null);
    } catch (error) {
      console.error('[confirmDelete] メッセージ削除エラー:', error);
      // TODO: ユーザーへのエラー通知を追加
    } finally {
      setIsDeleting(false);
    }
  }, [messageToDelete, projectId, isDeleting, deleteMessage]);

  // 削除キャンセル処理
  const cancelDelete = useCallback(() => {
    setMessageToDelete(null);
  }, []);

  // 編集キャンセル処理
  const cancelEdit = useCallback(() => {
    setMessageToEdit(null);
    setEditExistingFiles([]);
    setEditFilesChanged(false);
  }, []);

  // 返信キャンセル処理
  const cancelReply = useCallback(() => {
    setMessageToReply(null);
  }, []);

  // 編集時の既存ファイル削除ハンドラー
  const handleRemoveExistingFile = useCallback((fileId: string) => {
    setEditExistingFiles(prev => prev.filter(f => f.id !== fileId));
    setEditFilesChanged(true);
  }, []);

  // 本文中の [MM:SS] クリックで、直近の video/audio 添付にシーク再生する
  const {
    handleMediaRef,
    handlePlayStateChange,
    handleTimestampClick,
  } = useTimestampSeek(allMessages);

  // 既読処理を実行する関数（debounce後に呼ばれる）
  const processReadMessages = useCallback(async () => {
    if (!projectId || pendingReadMessageIds.current.size === 0 || !currentUserForReceivers) {
      return;
    }

    const messageIdsToRead = Array.from(pendingReadMessageIds.current);
    pendingReadMessageIds.current.clear();

    try {
      await markMessagesAsRead({
        project_id: projectId,
        message_ids: messageIdsToRead,
      }).unwrap();

      // 成功したらローカルステートを更新（receiversに自分を追加 + seen_at）
      setAllMessages(prev => prev.map(msg => {
        if (messageIdsToRead.includes(msg.id) && !msg.receivers.some(r => r.id === currentUserForReceivers.id)) {
          return {
            ...msg,
            receivers: [...msg.receivers, {
              ...currentUserForReceivers,
              seen_at: new Date().toISOString(),
            }],
          };
        }
        return msg;
      }));
    } catch (error) {
      // APIエラー時は失敗したメッセージIDを復元して再試行
      console.warn('[processReadMessages] 既読登録エラー、リトライ予定:', error);
      
      // 失敗したメッセージIDを復元
      messageIdsToRead.forEach(id => pendingReadMessageIds.current.add(id));
      
      // 3秒後にリトライ（短すぎるとサーバーに負荷がかかる）
      if (readDebounceTimer.current) {
        clearTimeout(readDebounceTimer.current);
      }
      readDebounceTimer.current = setTimeout(() => {
        processReadMessages();
      }, 3000);
    }
  }, [projectId, currentUserForReceivers, markMessagesAsRead]);

  // 既読キューにメッセージを追加する関数
  const addToReadQueue = useCallback((messageId: string) => {
    pendingReadMessageIds.current.add(messageId);

    // 既存のタイマーをクリア
    if (readDebounceTimer.current) {
      clearTimeout(readDebounceTimer.current);
    }

    // 500ms後に既読処理を実行
    readDebounceTimer.current = setTimeout(() => {
      processReadMessages();
    }, 500);
  }, [processReadMessages]);

  // ファイル一覧でのリネーム・差し替え結果をローカルの allMessages に反映する
  const handleFileUpdated = useCallback(
    (messageId: string, fileId: string, patch: Partial<ProjectMessageFile>) => {
      setAllMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          files: m.files.map(f => (f.id === fileId ? { ...f, ...patch } : f)),
        };
      }));
    },
    [],
  );

  // ファイル一覧での削除結果をローカルの allMessages に反映する
  const handleFileDeleted = useCallback((messageId: string, fileId: string) => {
    setAllMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      const files = m.files.filter(f => f.id !== fileId);
      return { ...m, files, has_files: files.length > 0 };
    }));
  }, []);

  // WS 受信ハンドラ等から reconnect フォールバック（一覧の取り直し）を呼ぶための ref。
  // handleReconnect は後段で定義されるため、定義順の循環を避けるため ref 経由で参照する。
  const handleReconnectRef = useRef<(() => void) | null>(null);

  // ファイル一覧での外部D&Dアップロード結果（新規ファイル）をローカルに反映する。
  // 非表示メッセージ（受領ファイル置き場）への初回アップロードでは対象メッセージが
  // ローカルに存在しないため、単体取得してマージする（WS の created 受信と同じ流れ）。
  const handleFileAdded = useCallback((messageId: string, file: ProjectMessageFile) => {
    if (!allMessagesRef.current.some(m => m.id === messageId)) {
      if (!projectId) return;
      fetchMessageDetail({ project_id: projectId, message_id: messageId }, false)
        .unwrap()
        .then((res) => {
          const fetched = extractDetailEntity<ProjectMessage>(res);
          if (!fetched) {
            handleReconnectRef.current?.();
            return;
          }
          setAllMessages(prev =>
            prev.some(m => m.id === fetched.id)
              ? prev.map(m => (m.id === fetched.id ? fetched : m))
              : sortMessagesByDate([...prev, fetched]),
          );
        })
        .catch(() => {
          // 単体取得に失敗しても一覧の取り直しでアップロード分を反映する
          handleReconnectRef.current?.();
        });
      return;
    }
    setAllMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      if (m.files.some(f => f.id === file.id)) return m;
      return { ...m, files: [...m.files, file], has_files: true };
    }));
  }, [projectId, fetchMessageDetail]);

  // ファイル一覧でのフォルダ作成結果をローカルに反映する
  const handleFolderCreated = useCallback((messageId: string, folder: ProjectMessageFolder) => {
    setAllMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      const folders = m.folders ?? [];
      if (folders.some(f => f.id === folder.id)) return m;
      return { ...m, folders: [...folders, folder] };
    }));
  }, []);

  // ファイル一覧でのフォルダ削除結果をローカルに反映する
  const handleFolderDeleted = useCallback((messageId: string, folderId: string) => {
    setAllMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      return { ...m, folders: (m.folders ?? []).filter(f => f.id !== folderId) };
    }));
  }, []);

  // ファイル一覧でのフォルダ情報更新（共有状態など）をローカルに反映する
  const handleFolderUpdated = useCallback(
    (messageId: string, folderId: string, patch: Partial<ProjectMessageFolder>) => {
      setAllMessages(prev => prev.map(m => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          folders: (m.folders ?? []).map(f =>
            f.id === folderId ? { ...f, ...patch } : f,
          ),
        };
      }));
    },
    [],
  );

  // ダウンロード既読の API 送信。一括 zip ダウンロード完了時はファイル数ぶん連続で呼ばれるため、
  // 短時間の呼び出しを配列 1 回の PATCH にまとめる（1 件ずつ送ると BE の WebSocket 配信が
  // 同時多発して Redis の接続上限に当たる）。
  const enqueueMarkDownloaded = useBatchedCall<string>(async (fileIds) => {
    if (!projectId) return;
    try {
      await markFilesAsDownloaded({
        project_id: projectId,
        downloaded_file_ids: fileIds,
      }).unwrap();
    } catch (error) {
      console.warn('[handleFileDownload] ダウンロード記録エラー:', error);
    }
  });

  // ファイルダウンロード記録（楽観的更新: UI即反映 → API送信）
  const handleFileDownload = useCallback((messageId: string, fileId: string) => {
    if (!projectId || !currentUserForReceivers) return;

    const messages = allMessagesRef.current;
    const msg = messages.find(m => m.id === messageId);

    // 既にダウンロード済みならスキップ
    const targetFile = msg?.files.find(f => f.id === fileId);
    if (targetFile?.downloaded_users?.some(u => u.id === currentUserForReceivers.id)) return;

    // receivers にある自分のデータ（BE由来のアバターURL）を優先使用
    const receiverSelf = msg?.receivers.find(r => r.id === currentUserForReceivers.id);
    const avatarUrl = receiverSelf?.avatar ?? currentUserForReceivers.avatar;

    // 楽観的更新（即座にUIに反映）
    setAllMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m;
      return {
        ...m,
        files: m.files.map(file => {
          if (file.id !== fileId) return file;
          return {
            ...file,
            downloaded_users: [
              ...(file.downloaded_users || []),
              {
                id: currentUserForReceivers.id,
                display_name: currentUserForReceivers.display_name,
                avatar: avatarUrl,
                downloaded_at: new Date().toISOString(),
              },
            ],
          };
        }),
      };
    }));

    enqueueMarkDownloaded(fileId);
  }, [projectId, currentUserForReceivers, enqueueMarkDownloaded]);

  // メッセージが未読かどうかを判定
  const isMessageUnread = useCallback((message: ProjectMessage): boolean => {
    if (!currentUserId) return false;
    // 自分のメッセージは対象外
    if (message.author?.id === currentUserId) return false;
    // 既に自分が既読済みなら対象外
    if (message.receivers.some(r => r.id === currentUserId)) return false;
    return true;
  }, [currentUserId]);

  // IntersectionObserverのセットアップ
  useEffect(() => {
    if (!isInitialScrollComplete || !currentUserId) return;

    // 既存のObserverをクリーンアップ
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    // 新しいObserverを作成
    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute('data-message-id');
            if (messageId) {
              // 対象メッセージを取得
              const message = allMessages.find(m => m.id === messageId);
              if (message && isMessageUnread(message)) {
                addToReadQueue(messageId);
              }
            }
          }
        });
      },
      {
        root: contentRef.current,
        rootMargin: '0px',
        threshold: 0.5, // 50%以上表示されたら既読とする
      }
    );

    // 全てのメッセージ要素を監視対象に追加
    messageElementRefs.current.forEach((element) => {
      intersectionObserverRef.current?.observe(element);
    });

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
      if (readDebounceTimer.current) {
        clearTimeout(readDebounceTimer.current);
      }
      // 注意: pendingReadMessageIds と messageElementRefs のクリアは
      // projectId変更時のuseEffectで行う（allMessages変更時にクリアすると未送信の既読が失われる）
    };
  }, [isInitialScrollComplete, currentUserId, allMessages, isMessageUnread, addToReadQueue]);

  // メッセージ要素のrefを登録するコールバック
  const registerMessageRef = useCallback((messageId: string, element: HTMLDivElement | null) => {
    if (element) {
      messageElementRefs.current.set(messageId, element);
      // IntersectionObserverが既に存在する場合は監視対象に追加
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.observe(element);
      }
    } else {
      const existingElement = messageElementRefs.current.get(messageId);
      if (existingElement && intersectionObserverRef.current) {
        intersectionObserverRef.current.unobserve(existingElement);
      }
      messageElementRefs.current.delete(messageId);
    }
  }, []);

  // ファイル選択ハンドラー（CommentFieldから呼ばれる）
  const handleFilesSelected = useCallback((files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  // ファイル削除ハンドラー（CommentFieldから呼ばれる）
  // index は allSelectedFiles の index なので、適切なリストから削除
  const handleRemoveFile = useCallback((index: number) => {
    const normalFilesCount = selectedFiles.length;
    if (index < normalFilesCount) {
      // 通常のファイルを削除
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      // フォルダからのファイルを削除
      const folderIndex = index - normalFilesCount;
      setSelectedFilesWithPath((prev) => prev.filter((_, i) => i !== folderIndex));
    }
  }, [selectedFiles.length]);

  // CommentFieldに渡す用の全選択ファイル（File[]形式）
  const allSelectedFiles = useMemo(() => {
    const normalFiles = selectedFiles;
    const folderFiles = selectedFilesWithPath.map(f => f.file);
    return [...normalFiles, ...folderFiles];
  }, [selectedFiles, selectedFilesWithPath]);

  // ドラッグ&ドロップハンドラー
  // ドロップされたファイルをselectedFilesに追加（送信ボタンで送信）
  const handleFileDrop = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  // フォルダドロップハンドラー（パス情報付き）
  const handleFileDropWithPath = useCallback((filesWithPath: FileWithPath[]) => {
    if (filesWithPath.length === 0) return;
    setSelectedFilesWithPath((prev) => [...prev, ...filesWithPath]);
  }, []);

  // アップロード進捗のキャンセルハンドラー
  const handleUploadCancel = useCallback((fileId: string) => {
    abortFile(fileId);
  }, [abortFile]);

  // 完了/エラーファイルのクリアハンドラー
  const handleUploadClear = useCallback(() => {
    clearCompleted();
  }, [clearCompleted]);

  // ─── WebSocket リアルタイム連携 ─────────────────────
  //
  // 他ユーザーの送信・編集・削除・既読・ダウンロード既読を `/ws/spa/events` 経由で受信し、
  // ローカルの allMessages を差分更新する。BE 仕様書「Messenger リアルタイム連携」§3 準拠。
  const { sendEvent } = useMessengerSocket();

  const handleWsEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (!projectId) return;
      switch (ev.type) {
        case "message.project.created":
        case "message.project.updated": {
          const { message_id } = ev.payload;
          // 単体取得して allMessages にマージ（新規は追加、既存は置換）。
          // レスポンス形状 `data: ProjectMessage` と `data: { message }` の揺れに
          // 備え extractDetailEntity で吸収する。取得失敗時は一覧全体を取り直す
          // ことで、BE 側のエンドポイント未実装や 404 の場合も取りこぼしを防ぐ。
          fetchMessageDetail({ project_id: projectId, message_id }, false)
            .unwrap()
            .then((res) => {
              const fetched = extractDetailEntity<ProjectMessage>(res);
              if (!fetched) {
                console.warn(
                  "[TalkRoom:WS] detail response shape unexpected",
                  res,
                );
                handleReconnectRef.current?.();
                return;
              }
              setAllMessages((prev) => {
                const exists = prev.some((m) => m.id === fetched.id);
                if (exists) {
                  return prev.map((m) => (m.id === fetched.id ? fetched : m));
                }
                return sortMessagesByDate([...prev, fetched]);
              });
            })
            .catch((err) => {
              console.warn(
                "[TalkRoom:WS] fetchMessageDetail failed, falling back to list refetch",
                err,
              );
              handleReconnectRef.current?.();
            });
          break;
        }
        case "message.project.deleted": {
          const { message_id } = ev.payload;
          setAllMessages((prev) => prev.filter((m) => m.id !== message_id));
          break;
        }
        case "message.project.read": {
          const { message_ids, reader_user_id } = ev.payload;
          if (!message_ids?.length) break;
          const idSet = new Set(message_ids);
          setAllMessages((prev) =>
            prev.map((msg) => {
              if (!idSet.has(msg.id)) return msg;
              // 冪等: 既に receivers に同 user_id がいれば no-op。
              if (msg.receivers.some((r) => r.id === reader_user_id)) return msg;
              // BE 仕様書 §3.3: display_name / avatar は payload に無いので、
              // 既知のメッセージ作成者・既読者から情報を引く。見つからなければ
              // プレースホルダのユーザー情報を入れておく（次回の REST 取得で上書き）。
              const knownAuthor =
                msg.author?.id === reader_user_id ? msg.author : undefined;
              const knownReceiver = msg.receivers.find((r) => r.id === reader_user_id);
              const user: ProjectMessageUser = knownReceiver ?? knownAuthor ?? {
                id: reader_user_id,
                display_name: "",
                avatar: null,
                position: "CLIENT",
              };
              return {
                ...msg,
                receivers: [
                  ...msg.receivers,
                  { ...user, seen_at: new Date().toISOString() },
                ],
              };
            }),
          );
          break;
        }
        case "message.project.download_read": {
          const { message_ids, file_ids, reader_user_id } = ev.payload;
          if (!message_ids?.length || !file_ids?.length) break;
          const msgIdSet = new Set(message_ids);
          const fileIdSet = new Set(file_ids);
          setAllMessages((prev) =>
            prev.map((msg) => {
              if (!msgIdSet.has(msg.id)) return msg;
              return {
                ...msg,
                files: msg.files.map((file) => {
                  if (!fileIdSet.has(file.id)) return file;
                  if (file.downloaded_users?.some((u) => u.id === reader_user_id))
                    return file;
                  const knownReceiver = msg.receivers.find(
                    (r) => r.id === reader_user_id,
                  );
                  return {
                    ...file,
                    downloaded_users: [
                      ...(file.downloaded_users || []),
                      {
                        id: reader_user_id,
                        display_name: knownReceiver?.display_name ?? "",
                        avatar: knownReceiver?.avatar ?? null,
                        downloaded_at: new Date().toISOString(),
                      },
                    ],
                  };
                }),
              };
            }),
          );
          break;
        }
        default:
          break;
      }
    },
    [projectId, fetchMessageDetail],
  );

  useMessengerSubscribe("project", projectId, handleWsEvent);

  // allMessages に現れる author / receivers / downloaded_users を Redux userDirectory に
  // 集約し、後続の WS read / download_read で未知ユーザーが来てもアイコンを解決できる
  // ようにする。
  const directoryUsers = useMemo(
    () => collectMessengerUsers(allMessages),
    [allMessages],
  );
  useSyncUserDirectory(directoryUsers);

  // 再接続時はこのトークルームの一覧を取り直す（切断中の取りこぼしカバー）。
  const handleReconnect = useCallback(() => {
    if (!projectId) return;
    fetchMoreMessages({ project_id: projectId, limit: 20, offset: 0 }, false)
      .unwrap()
      .then((result) => {
        if (!result?.data?.messages) return;
        hasInitializedMessages.current = true;
        setAllMessages(sortMessagesByDate(result.data.messages));
        setHasMore(result.data.has_next);
        setCurrentOffset(result.data.next_offset || 0);
      })
      .catch((err) => {
        console.warn("[TalkRoom:WS] reconnect refetch failed", err);
      });
  }, [projectId, fetchMoreMessages]);

  handleReconnectRef.current = handleReconnect;

  useMessengerReconnect(handleReconnect);

  // Typing 送信（scope: project）。
  const { onTypingChange, onTypingCommit } = useTypingSender({
    send: sendEvent,
    scope: "project",
    ids: {
      project_id: projectId || "",
      parent_id: messageToReply?.id ?? null,
    },
    enabled: Boolean(projectId),
  });

  // Typing 受信表示用のユーザー名解決。まずはメッセージ作成者から逆引き、
  // 見つからなければメンバー一覧（現ユーザーのみ既知）でフォールバック。
  const resolveTypingUserName = useCallback(
    (userId: number): string | undefined => {
      if (currentUserId === userId && currentUserForReceivers) {
        return currentUserForReceivers.display_name;
      }
      const fromMessage = allMessagesRef.current.find(
        (m) => m.author?.id === userId,
      )?.author;
      if (fromMessage?.display_name) return fromMessage.display_name;
      const fromReceiver = allMessagesRef.current
        .flatMap((m) => m.receivers)
        .find((r) => r.id === userId);
      return fromReceiver?.display_name;
    },
    [currentUserId, currentUserForReceivers],
  );

  return (
    <TalkRoomContainer>
      {/* フィルターバー（スクロールコンテンツの上にフロート） */}
      <TopAppBarFloat>
        <TopAppBarHome
          options={talkroomFilterOptions}
          defaultValue={firstAvailableMode}
          value={filterMode}
          onChange={handleFilterModeChange}
          showSearchInput={true}
          onSearchChange={handleSearchTextChange}
          searchPlaceholder={filterMode === "files" ? "ファイル名・フォルダ名で検索..." : "メッセージを検索..."}
          leftIcon="files"
          rightActions={<ProjectMenu />}
        />
      </TopAppBarFloat>

      {/* ファイル一覧（ストレージビュー） */}
      {filterMode === "files" && (
        projectId ? (
          <TalkRoomFileTable
            projectId={projectId}
            topInset={TOP_APP_BAR_HOME_HEIGHT}
            messages={allMessages}
            myPosition={talkroomPosition}
            currentUserId={currentUserId}
            isLoadingAll={hasMore || isLoadingMore || isLoadingMessages}
            searchText={searchText}
            onFileDownload={handleFileDownload}
            onFileUpdated={handleFileUpdated}
            onFileDeleted={handleFileDeleted}
            onFileAdded={handleFileAdded}
            onFolderCreated={handleFolderCreated}
            onFolderDeleted={handleFolderDeleted}
            onFolderUpdated={handleFolderUpdated}
          />
        ) : null
      )}

      {filterMode !== "files" && (
      <DropZoneOverlay
        enabled={!isLoadingMessages}
        onDrop={handleFileDrop}
        overlayText="ファイルまたはフォルダをドロップしてアップロード"
        dragActiveText="ここにドロップ"
        enableFolderDrop={true}
        onDropWithPath={handleFileDropWithPath}
      >
        <TalkRoomScroll ref={contentRef} $bottomPadding={talkRoomBottomPadding}>
          <TalkRoomContent>
            {/* ローディング中の表示 */}
            {isLoadingMessages ? (
              <SvgLoadingAnimation />
            ) : (
              <>
                {/* 上部ローディングインジケーター */}
                <InlineLoading visible={isLoadingMore} size={32} />

                {/* トークルームのメインコンテンツ（古い順→新しい順） */}
                <LayoutGroup key={filterMode}>
                  {filteredMessages.map((message, index) => {
                    const isOurs = isMessageOnRight(message);
                    const prevMessage = filteredMessages[index - 1];
                    const isSameAuthorAsPrev = !!prevMessage && prevMessage.author?.id === message.author?.id;
                    const parentMessage = message.parent_id
                      ? allMessages.find(msg => msg.id === message.parent_id) || null
                      : null;
                    const isFadingOut = fadingOutEntry?.id === message.id;
                    const fadeClass = isFadingOut
                      ? fadingOutEntry.direction === "right" ? "fade-out-right" : "fade-out-left"
                      : undefined;

                    return (
                      <motion.div
                        key={message.id}
                        layout
                        transition={{ layout: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } }}
                        data-message-id={message.id}
                        ref={(el) => registerMessageRef(message.id, el)}
                        className={fadeClass}
                        style={isFadingOut ? { pointerEvents: "none" } : undefined}
                      >
                        <MessageItem
                          message={message}
                          isOurs={isOurs}
                          showAvatar={!isSameAuthorAsPrev}
                          showActions={true}
                          parentMessage={parentMessage}
                          currentUserId={currentUserId}
                          onReply={handleReply}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onResolve={handleResolve}
                          onFileDownload={handleFileDownload}
                          onTimestampClick={(seconds, mediaItemId, frames) => handleTimestampClick(seconds, mediaItemId, message.id, frames)}
                          onMediaRef={handleMediaRef}
                          onPlayStateChange={handlePlayStateChange}
                          filterMode={filterMode === "search" ? "in_progress" : filterMode} // 検索中は進行中と同じ表示
                        />
                      </motion.div>
                    );
                  })}
                </LayoutGroup>

                {/* スクロール位置調整用の余白要素 */}
                <MessageEndSpacer />
              </>
            )}
          </TalkRoomContent>
        </TalkRoomScroll>
      </DropZoneOverlay>
      )}

      {/* アップロード進捗モーダル */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            // アップロード中はモーダルを閉じない
            const isUploading = uploadingFiles.some(f => f.status === 'uploading' || f.status === 'pending');
            if (!isUploading) {
              handleUploadClear();
            }
          }}
          size="default"
        >
          <UploadModalTitle>ファイルアップロード</UploadModalTitle>
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={handleUploadCancel}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗の解決ダイアログ */}
      {uploadFailureDialog}

      {/* CommentFieldコンポーネントをActionPanelで囲む（ファイル一覧では非表示） */}
      {filterMode !== "files" && (
      <ActionPanel>
        <div ref={commentFieldRef} className="u-w100 u-col u-row-center">
          {/* 提出 / 資料を送る（CommentField と同じ最大幅に揃える） */}
          <div className="u-w100" style={{ maxWidth: 740 }}>
            <ProjectPrimaryActionSlot />
          </div>
          <div className="u-w100 u-row-center">
            <TypingIndicator
              scope="project"
              scopeKey={projectId}
              currentUserId={currentUserId}
              resolveUserName={resolveTypingUserName}
            />
          </div>
          <CommentField
            showPinIcon={false}
            showAttachIcon={true}
            onSend={handleSendMessage}
            editMode={!!messageToEdit}
            initialValue={messageToEdit?.message || ''}
            onCancel={messageToEdit ? cancelEdit : cancelReply}
            replyMode={!!messageToReply}
            replyToMessage={messageToReply}
            onFilesSelected={handleFilesSelected}
            onFilesDroppedWithPath={handleFileDropWithPath}
            selectedFiles={allSelectedFiles}
            onRemoveFile={handleRemoveFile}
            multipleFiles={true}
            showIconsInEditMode={true}
            existingFiles={messageToEdit ? editExistingFiles : undefined}
            onRemoveExistingFile={messageToEdit ? handleRemoveExistingFile : undefined}
            onTypingChange={onTypingChange}
            onTypingCommit={onTypingCommit}
            enableChikuchiku={isTalkroomClientSide}
            chikuchikuScene="chikuchiku_filter_talkroom"
            chikuchikuContext={{
              project_id: projectId,
              exclude_comment_id: messageToEdit?.id ?? null,
            }}
            enableReplyAssistant={isTalkroomProducerSide}
            replyAssistantScene="reply_assistant_talkroom"
            replyAssistantContext={{
              project_id: projectId,
              exclude_comment_id: messageToEdit?.id ?? null,
            }}
            initialProofread={messageToEdit?.proofread ?? null}
          />
        </div>
      </ActionPanel>
      )}

      {/* 削除確認ダイアログ */}
      {messageToDelete && (
        <ConfirmModal
          message="このメッセージを削除しますか？"
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </TalkRoomContainer>
  );
};

export default TalkRoom;
